import React, {useState, useEffect, useContext} from "react";
import { Link } from "react-router-dom";
import "./AdminDashboard.css";
import Navigation from "./Navigation";
import Error from "./Error";

import { fetchStudents, fetchUser, userRoles } from "../api/user-service";
import { 
    Box, 
    CircularProgress,
    Paper,
    Stack,
    TableContainer,
    Typography,
} from "@mui/material";

// Custom Components
import ListStudentsTable    from "./tables/ListStudentsTable";
import LoadingIcon          from "./loading/LoadingIcon";
import StudentActivityTable from "./tables/StudentActivityTable";

// Contexts
import { UserContext } from "../Contexts";

// Custom Scripts
import { LoadedDataState } from "../scripts/loadedData/loadedData";



const AdminDashboard = () => {

    // ------------------------------------------------------------------------
    //   Variables
    // ------------------------------------------------------------------------

    // Context
    const { userData } = useContext( UserContext )

    // State
    const [students, setStudents] = useState([]);
    const [studentsLoading, setStudentsLoading] = useState([]);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const [ selectedStudent, setSelectedStudent ] = useState(null);


    // ------------------------------------------------------------------------
    //   Setup Effects
    // ------------------------------------------------------------------------

    // Get students
    useEffect(() => {
        fetchStudents()
        .then(students => {
            students.sort((a, b) => a.username.localeCompare(b.username))
            setStudents(students)
            setStudentsLoading(false);
        },
        error => setError(error.message));
    }, []);

    // Combine user & students error/loading states into one
    useEffect(() => {
        if ( userData.state === LoadedDataState.FAILURE ) {
            setError( userData.error.message );
        }
        else {
            setIsLoading( (userData.state === LoadedDataState.WORKING) || studentsLoading )
        }
    }, [ students, userData ])


    // ------------------------------------------------------------------------
    //   Special Cases
    // ------------------------------------------------------------------------

    // State Check: Error
    // If there was an error loading the page, show just the error message
    if (error || userData.state === LoadedDataState.FAILURE) {
        return <Error message={error} />
    }

    // State Check: Loading
    // If the page data is still loading, show the progress icon
    if (isLoading || (userData.state === LoadedDataState.NONE) || (userData.state === LoadedDataState.WORKING)) {
        return (
            <LoadingIcon />
        )
    }

    // State Check: Authorization
    // If the current user does not have the "ADMIN" role, prevent them from seeing page content
    if ( userData.state === LoadedDataState.SUCCESS && userData.value.role !== userRoles.ADMIN) {
        return (
            <Box>
                <Typography variant="h5">You are not authorized to view this page. Go to <a href="/login">homepage</a>.</Typography>
            </Box>
        )
    }


    // ------------------------------------------------------------------------
    //   Main Component
    // ------------------------------------------------------------------------

    return (
        <Box sx={{marginLeft: 0, paddingBottom: {xs: 4, md: 0}}}>

            {/* Top Message */}
            <Typography variant="h6">Check out participants activities</Typography>

            <Stack direction={{xs: "column", md: "row"}} gap={4} marginTop={4} maxHeight={{xs: "none", md: "65vh"}}>

                {/* Student Table */}
                {!students.length ? <Typography>No students found.</Typography> : (
                    <TableContainer component={Paper} elevation={4} sx={{
                        maxHeight: "min(100%, fit-content)",
                        maxWidth: {
                            xs: 'unset',
                            md: '30rem',
                        },
                    }}>
                        <ListStudentsTable students={students} selected={selectedStudent} setSelected={setSelectedStudent} />
                    </TableContainer>
                )}

                <TableContainer component={Paper} elevation={4} sx={{
                    maxHeight: {xs: '80vh', md: '100%'},
                }}>
                    {
                        selectedStudent
                            ? <StudentActivityTable studentID={selectedStudent?.id} studentName={selectedStudent?.username} />
                            : (
                                <Box display="flex" justifyContent="center" alignItems="center" height="100%" padding={3} textAlign="center" boxSizing="border-box">
                                    <Typography>Choose a student to view their recorded activity.</Typography>
                                </Box>
                            )
                    }
                </TableContainer>
            </Stack>
        </Box>
    );
};

export default AdminDashboard;